import React, { useState, useEffect } from 'react';
import { Droplet, Heart, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import GenderAvatar from './ui/gender-avatar';
import axios from 'axios';
import { calculateAge } from '../services/userService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* --------------------------------------------------------------------------
   Skeleton card for loading state
-------------------------------------------------------------------------- */
const DonorCardSkeleton = () => (
  <div className="flex flex-col items-center p-5 rounded-2xl border border-gray-100 bg-gray-50">
    <div className="skeleton w-16 h-16 rounded-full mb-3" />
    <div className="skeleton skeleton-text w-20 mb-1 rounded" />
    <div className="skeleton skeleton-text w-28 mb-1 rounded" />
    <div className="skeleton h-5 w-16 rounded-full" />
  </div>
);

const RecentDonorsFeed = () => {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentDonors();
  }, []);

  const fetchRecentDonors = async () => {
    try {
      const response = await axios.get(`${API}/donors/recent?limit=10`);
      setDonors(response.data || []);
    } catch (error) {
      // handled silently
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getLastName = (fullName) => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(/\s+/);
    return nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
  };

  const getDonationTypes = (donor) => {
    const types = [];
    if (donor.is_blood_donor || donor.isBloodDonor) types.push('blood');
    if (donor.is_organ_donor || donor.isOrganDonor) types.push('organ');
    if (donor.body_donation || donor.bodyDonation) types.push('body');
    return types;
  };

  const getDonorTypeLabel = (types) => {
    if (types.length === 0) return '';
    const labels = { blood: 'Blood', organ: 'Organ', body: 'Body' };
    return types.map(t => labels[t]).join(' & ');
  };

  if (!loading && donors.length === 0) {
    return null; // Hide section entirely if no donors
  }

  return (
    <Card className="shadow-lg border-0 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-red-50 to-white pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-600" aria-hidden="true" fill="currentColor" />
              Recent Donors
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Join these amazing people making a difference</p>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs text-gray-400">
            <Globe className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Worldwide</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div role="status" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" aria-label="Loading recent donors" aria-busy="true">
            {Array.from({ length: 10 }).map((_, i) => (
              <DonorCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div
            role="feed"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4"
            aria-label={`${donors.length} recent donors`}
          >
            {donors.slice(0, 10).map((donor, index) => {
              const country = donor.country || 'Unknown';
              const donationTypes = getDonationTypes(donor);
              const createdDate = formatDate(donor.created_at || donor.createdAt);
              const profilePicture = donor.profile_picture || donor.profilePicture;
              const gender = donor.gender;

              const fullName = donor.name || '';
              const lastName = getLastName(fullName);
              const birthYear = donor.birth_year || donor.birthYear;
              const age = birthYear ? calculateAge(birthYear) : null;
              const typeLabel = getDonorTypeLabel(donationTypes);

              // Country display — ISO code preferred for brevity
              const countryDisplay = country.length > 12
                ? country.split(' ')[0]
                : country;

              return (
                <div
                  key={donor.id}
                  className="flex flex-col items-center p-4 rounded-2xl border border-gray-100 bg-white hover:border-red-200 hover:shadow-md transition-all duration-200 animate-fade-in-up group"
                  style={{ animationDelay: `${index * 50}ms` }}
                  role="article"
                  aria-label={`${lastName}${age ? `, age ${age}` : ''}, donor from ${country}`}
                >
                  {/* Avatar with ring */}
                  <div className="relative mb-3">
                    <div className="p-0.5 rounded-full bg-gradient-to-br from-red-400 to-red-700">
                      <div className="bg-white rounded-full p-0.5">
                        <GenderAvatar
                          profilePicture={profilePicture}
                          gender={gender}
                          size={56}
                          className="w-14 h-14 rounded-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Donor info */}
                  <div className="text-center space-y-1 w-full">
                    <p className="text-sm font-semibold text-gray-900 truncate">{lastName}</p>
                    {age && (
                      <p className="text-xs text-gray-500">Age {age}</p>
                    )}
                    <p className="text-xs text-gray-400 truncate">{countryDisplay}</p>

                    {/* Donation type badges */}
                    {typeLabel && (
                      <div className="flex items-center justify-center gap-1 pt-1 flex-wrap">
                        {donationTypes.includes('blood') && (
                          <span className="inline-flex items-center gap-0.5 bg-red-50 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                            <Droplet className="w-3 h-3" aria-hidden="true" />
                            Blood
                          </span>
                        )}
                        {donationTypes.includes('organ') && (
                          <span className="inline-flex items-center gap-0.5 bg-pink-50 text-pink-700 text-xs px-2 py-0.5 rounded-full font-medium">
                            <Heart className="w-3 h-3" aria-hidden="true" />
                            Organ
                          </span>
                        )}
                      </div>
                    )}

                    {createdDate && (
                      <p className="text-xs text-gray-300 mt-1">{createdDate}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentDonorsFeed;
